import {
  ComplianceAutoApprovalAttemptDecision,
  ExternalIngestionDocumentFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  RuleType,
  VariableSource,
} from '@client/graphql/__generated__/types';
import { MultiFieldValue } from '@common/graphql';
import {
  getContractDocumentPageCount,
  getContractDocumentPageOrder,
  getContractDocumentPageRange,
  getPageNumbers,
  toDocumentRelativePages,
} from '@common/utils/validation';
import { TITLE_TERMINATORS, titleCase } from 'title-case';
import type {
  ComplianceIngestionDocumentValidation,
  ComplianceValidationReportData,
} from '../Compliance.types';
import { getFieldLevelComparisonData } from './fieldComparison.utils';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';

export interface ValidationItem {
  message: string;
  ruleUuid: string;
  ruleType?: RuleType;
  /**
   * Failing pages as ABSOLUTE pages in the ingestion document's combined PDF.
   * Keep using these for anything that indexes the rendered PDF — field lookups,
   * overlays, scroll targets — since those are all keyed on absolute pages.
   */
  pageNumbers?: number[];
  /**
   * The same pages renumbered relative to this item's contract document, for
   * DISPLAY only: a combined PDF should tell the user "Page 3" of the addendum,
   * not "Page 25" of the bundle (BLO-13411). Falls back to the absolute pages
   * when the document's page range isn't known.
   */
  documentPageNumbers?: number[];
  /**
   * Page count of this item's contract document alone (not the whole combined
   * PDF), so single-page documents can drop the page prefix. `0` when unknown.
   */
  documentPageCount?: number;
  contractDocumentUuid?: string;
  mappingKeys?: string[];
  ingestionDocumentValidationId?: string;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
}

export interface ValidationResult {
  ruleUuid: string;
  output: boolean;
  message: string;
  ruleType?: RuleType;
  /** @deprecated legacy single-page; read via getPageNumbers(). */
  pageNumber?: number;
  pageNumbers?: number[];
  contractDocumentUuid?: string;
  fieldMappingKey?: string;
  focusFieldMappingKeys?: string[] | null;
  ruleFormula?: string;
  isDocumentValidation?: boolean;
  isPipelineValidation?: boolean;
  isOrganizationRule?: boolean;
  isOrganizationIgnored?: boolean;
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[];
  documentVersionFieldUuid?: string;
}

export interface ValidationResultsForDocument {
  errors: ValidationItem[];
  successes: ValidationItem[];
  organizationIgnoredRules?: ValidationItem[];
}

/**
 * The lowest ABSOLUTE page a validation item is attributed to. Used to look up
 * field geometry (which is keyed on absolute pages), NOT as a sort key — see
 * {@link sortValidationItemsByPage}. Items with no page attribution return
 * +Infinity.
 */
function getFirstPageNumber(item: ValidationItem): number {
  return item.pageNumbers?.length
    ? Math.min(...item.pageNumbers)
    : Number.POSITIVE_INFINITY;
}

/**
 * The lowest DOCUMENT-RELATIVE page a validation item is attributed to — the
 * page the item actually renders (BLO-13411) — so the list reads in the same
 * order as the page numbers on screen. Falls back to the absolute pages when
 * the item's contract document has no known page range, which is exactly what
 * `documentPageNumbers` already degrades to.
 */
function getFirstDocumentPageNumber(item: ValidationItem): number {
  const pages = item.documentPageNumbers?.length
    ? item.documentPageNumbers
    : item.pageNumbers;

  return pages?.length ? Math.min(...pages) : Number.POSITIVE_INFINITY;
}

/**
 * An item's contract document's position in the combined PDF, used as the
 * primary sort key so items group document by document. Items whose contract
 * document isn't in the order map (unknown page range, or a fragment that
 * doesn't select `contractDocumentPages`) sort last; when NO document is in the
 * map they all tie, and ordering falls through to the page keys — i.e. the
 * pre-grouping behaviour.
 */
function getContractDocumentSortValue(
  item: ValidationItem,
  contractDocumentOrder: Map<string, number>
): number {
  if (!item.contractDocumentUuid) return Number.POSITIVE_INFINITY;

  return (
    contractDocumentOrder.get(item.contractDocumentUuid) ??
    Number.POSITIVE_INFINITY
  );
}

interface FieldPosition {
  pageNumber: number;
  /** Normalized, top-origin vertical offset — smaller is higher on the page. */
  y: number;
}

/** `contractDocumentUuid` → `mappingKey` → field positions on each page. */
type FieldPositionLookup = Map<string, Map<string, FieldPosition[]>>;

/**
 * Builds a lookup of field geometry from the repositioned `fields` map, used
 * to order same-page items top-to-bottom.
 *
 * The geometry is only present on the GetIngestionDocument fragment (the side
 * panel / extension); other fragments (e.g. the overview's External document)
 * omit it. When absent we return an empty lookup — the within-page tiebreak
 * becomes a no-op and items keep page-level ordering. We never fetch geometry
 * that isn't already on the document.
 */
function buildFieldPositionLookup(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment
): FieldPositionLookup {
  const lookup: FieldPositionLookup = new Map();
  const fields =
    'fields' in document
      ? (document.fields as Record<string, unknown> | null | undefined)
      : undefined;

  if (!fields) return lookup;

  for (const [contractDocumentUuid, rawFields] of Object.entries(fields)) {
    if (!Array.isArray(rawFields)) continue;

    const positionsByMappingKey = new Map<string, FieldPosition[]>();
    for (const rawField of rawFields as Array<{
      mappingKey?: unknown;
      pageNumber?: unknown;
      y?: unknown;
    }>) {
      const { mappingKey, pageNumber, y } = rawField;
      if (
        typeof mappingKey !== 'string' ||
        typeof pageNumber !== 'number' ||
        typeof y !== 'number'
      ) {
        continue;
      }

      const positions = positionsByMappingKey.get(mappingKey) ?? [];
      positions.push({ pageNumber, y });
      positionsByMappingKey.set(mappingKey, positions);
    }

    lookup.set(contractDocumentUuid, positionsByMappingKey);
  }

  return lookup;
}

/**
 * Vertical position of an item's topmost field on `page`, used to order items
 * within a single page (smaller = higher = first).
 *
 * Returns +Infinity ("sort last within the page") for items we can't place:
 * multi-page items, document-level items with no field, and items whose fields
 * aren't in the lookup (e.g. fragments without geometry). Best-effort by
 * design — it only refines ordering where the position already exists.
 */
function getWithinPageSortValue(
  item: ValidationItem,
  page: number,
  lookup: FieldPositionLookup
): number {
  if (item.pageNumbers?.length !== 1) return Number.POSITIVE_INFINITY;
  if (!item.contractDocumentUuid || !item.mappingKeys?.length) {
    return Number.POSITIVE_INFINITY;
  }

  const positionsByMappingKey = lookup.get(item.contractDocumentUuid);
  if (!positionsByMappingKey) return Number.POSITIVE_INFINITY;

  let topY = Number.POSITIVE_INFINITY;
  for (const mappingKey of item.mappingKeys) {
    const positions = positionsByMappingKey.get(mappingKey);
    if (!positions) continue;

    for (const position of positions) {
      if (position.pageNumber === page && position.y < topY) {
        topY = position.y;
      }
    }
  }

  return topY;
}

/**
 * Orders validation items so the list walks the ingestion document one contract
 * document at a time, in three keys:
 *
 * 1. The item's contract document's position in the combined PDF, so a
 *    multi-document PDF never interleaves two forms' items — which would render
 *    as page numbers restarting mid-list, since each item shows its
 *    document-relative page (BLO-13411).
 * 2. The item's first document-relative page, matching the on-screen label.
 * 3. Its topmost field's vertical position when geometry is available, so
 *    same-page items read top-to-bottom. This key stays on ABSOLUTE pages —
 *    the geometry lookup is built from the PDF's own page numbers.
 *
 * Multi-page and position-less items fall last within their page. Stable: items
 * that tie on all three keys keep their original relative order.
 */
function sortValidationItemsByPage(
  items: ValidationItem[],
  fieldPositionLookup: FieldPositionLookup,
  contractDocumentOrder: Map<string, number>
): ValidationItem[] {
  return [...items].sort((a, b) => {
    const aDoc = getContractDocumentSortValue(a, contractDocumentOrder);
    const bDoc = getContractDocumentSortValue(b, contractDocumentOrder);
    if (aDoc !== bDoc) return aDoc - bDoc;

    const aPage = getFirstDocumentPageNumber(a);
    const bPage = getFirstDocumentPageNumber(b);
    if (aPage !== bPage) return aPage - bPage;

    const aY = getWithinPageSortValue(
      a,
      getFirstPageNumber(a),
      fieldPositionLookup
    );
    const bY = getWithinPageSortValue(
      b,
      getFirstPageNumber(b),
      fieldPositionLookup
    );
    if (aY === bY) return 0;

    return aY - bY;
  });
}

/**
 * Orders an ingestion document's contract documents by where each first appears
 * in the combined PDF, so the per-document sections/tabs on the compliance
 * surfaces follow the bundle instead of the `contractDocuments` relation's
 * arbitrary database order.
 *
 * Contract documents with no known page range keep their original relative
 * order at the end (the sort is stable), so an unclassified or partially mapped
 * document is never dropped or hoisted.
 */
export function sortContractDocumentsByPageOrder<T extends { id: string }>(
  contractDocuments: readonly T[],
  contractDocumentPages?: Record<string, unknown> | null
): T[] {
  const order = getContractDocumentPageOrder(contractDocumentPages);
  if (order.size === 0) return [...contractDocuments];

  return [...contractDocuments].sort((a, b) => {
    const aOrder = order.get(a.id) ?? Number.POSITIVE_INFINITY;
    const bOrder = order.get(b.id) ?? Number.POSITIVE_INFINITY;
    if (aOrder === bOrder) return 0;

    return aOrder - bOrder;
  });
}

/**
 * Extracts validation results for a specific document.
 *
 * @param document - The ingestion document to get validation results for
 * @param validationData - Optional validation data containing document validations
 * @param contractDocumentId - Optional specific contract document ID to filter results
 * @param hiddenDocumentValidationReviews - Optional review records marking
 *   errors as hidden. Any error matching a hidden review (same `ruleUuid`,
 *   `contractDocumentUuid`, and `ingestionDocumentValidation.id`) is removed
 *   from the returned `errors`, mirroring
 *   {@link getErrorAndSuccessCountsForContractDocument}.
 * @returns Object containing arrays of error, success, and org-ignored validation items
 *
 * @example
 * const { errors, successes } = getValidationResultsForDocument(
 *   document,
 *   validationData,
 *   'contract-123'
 * );
 * Log.info(`Found ${errors.length} errors and ${successes.length} successes`);
 */
export function getValidationResultsForDocument(
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): ValidationResultsForDocument {
  if (!document || !validationData?.documentValidations) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  // Get contract document UUIDs from the document
  const contractDocumentUuids = new Set(
    document.contractDocuments?.map((cd: { id: string }) => cd.id) || []
  );

  if (contractDocumentUuids.size === 0) {
    return { errors: [], successes: [], organizationIgnoredRules: [] };
  }

  // Per-contract-document page footprints, used to renumber each item's failing
  // pages for display. Read defensively: the field is a JSON scalar and is not
  // present on every document fragment this helper accepts.
  const contractDocumentPages = (
    document as {
      contractDocumentPages?: Record<string, unknown> | null;
    }
  ).contractDocumentPages;

  const errors: ValidationItem[] = [];
  const successes: ValidationItem[] = [];
  const organizationIgnoredRules: ValidationItem[] = [];
  const errorIds = new Set<string>();
  const successIds = new Set<string>();
  const organizationIgnoredRuleIds = new Set<string>();

  // Process each document validation
  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      // Handle the dynamic Record structure from GraphQL
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      // Prioritize AI validation results, but fall back to regular validation results if AI results are empty
      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          // Only include results for this document's contract documents
          if (
            result.contractDocumentUuid &&
            contractDocumentUuids.has(result.contractDocumentUuid)
          ) {
            // If contractDocumentId is provided, filter by specific contract document
            if (
              contractDocumentId &&
              result.contractDocumentUuid !== contractDocumentId
            ) {
              return;
            }

            // Use focusFieldMappingKeys if present, otherwise fall back to inputValues
            const mappingKeys =
              result.focusFieldMappingKeys ??
              result.inputValues?.reduce((acc, val) => {
                if (val.variableSource === VariableSource.FIELD) {
                  acc.push(val.fieldKey);
                }

                return acc;
              }, [] as string[]) ??
              (result.fieldMappingKey ? [result.fieldMappingKey] : []);

            // Ensure inputValues have properly serialized values
            const normalizedInputValues = result.inputValues?.map((input) => {
              let normalizedValue: string;
              if (typeof input.value === 'string') {
                normalizedValue = input.value;
              } else if (
                typeof input.value === 'object' &&
                input.value !== null
              ) {
                // GraphQL sometimes returns objects instead of JSON strings
                normalizedValue = JSON.stringify(input.value);
              } else {
                normalizedValue = String(input.value);
              }

              return {
                ...input,
                value: normalizedValue,
              };
            });

            const absolutePageNumbers = getPageNumbers(result);
            const pageRange = getContractDocumentPageRange(
              contractDocumentPages,
              result.contractDocumentUuid
            );

            const item: ValidationItem = {
              message: result.message,
              ruleUuid: result.ruleUuid,
              ruleType: result.ruleType,
              pageNumbers: absolutePageNumbers,
              documentPageNumbers: toDocumentRelativePages(
                absolutePageNumbers,
                pageRange
              ),
              documentPageCount: getContractDocumentPageCount(pageRange),
              contractDocumentUuid: result.contractDocumentUuid,
              mappingKeys,
              inputValues: normalizedInputValues,
              ingestionDocumentValidationId: docValidation.id,
              ruleFormula: result.ruleFormula,
              isDocumentValidation: result.isDocumentValidation,
              isPipelineValidation: result.isPipelineValidation,
              isOrganizationRule: result.isOrganizationRule,
              isOrganizationIgnored: result.isOrganizationIgnored,
            };

            // De-dupe per CONTRACT DOCUMENT, not per rule. Results are stored
            // twice, so exact repeats within one contract document must still
            // collapse — but the same rule failing on two contract documents is
            // two distinct findings on different pages, and keying on `ruleUuid`
            // alone discarded the second. That cost the extension its red overlay
            // for the second document (the surviving item's pages also drive the
            // overlay filter) and dropped a line from the agent's resubmit note.
            //
            // Only the callers that omit `contractDocumentId` ever saw the loss;
            // for the per-contract-document callers every item shares one uuid,
            // so this key behaves exactly as the old one did.
            //
            // A falsy `ruleUuid` maps to '' so the add-guards below keep their
            // current behaviour verbatim: errors/successes never de-dupe such
            // items (their `.add` is guarded), org-ignored ones still do.
            const dedupeKey = item.ruleUuid
              ? `${item.contractDocumentUuid ?? ''}|${item.ruleUuid}`
              : '';

            if (item.isOrganizationIgnored) {
              if (!organizationIgnoredRuleIds.has(dedupeKey)) {
                organizationIgnoredRules.push(item);
                organizationIgnoredRuleIds.add(dedupeKey);
              }

              return;
            }

            if (result.output && !errorIds.has(dedupeKey)) {
              // Drop errors hidden by a review (same rule + contract document +
              // validation version), mirroring the count helper's matching.
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === item.ruleUuid &&
                  review.contractDocumentUuid === item.contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id ===
                    item.ingestionDocumentValidationId &&
                  review.isHidden
              );

              if (!isHidden) {
                errors.push(item);

                if (item.ruleUuid) {
                  errorIds.add(dedupeKey);
                }
              }
            }

            if (!result.output && !successIds.has(dedupeKey)) {
              successes.push(item);

              if (item.ruleUuid) {
                successIds.add(dedupeKey);
              }
            }
          }
        });
      }
    }
  );

  const fieldPositionLookup = buildFieldPositionLookup(document);
  // Groups items by contract document before page, which matters on the flat
  // surfaces (extension, agent review) that ask for every contract document at
  // once. When callers pass a `contractDocumentId` the list is already
  // single-document, so this key just ties.
  const contractDocumentOrder = getContractDocumentPageOrder(
    contractDocumentPages
  );

  return {
    errors: sortValidationItemsByPage(
      errors,
      fieldPositionLookup,
      contractDocumentOrder
    ),
    successes: sortValidationItemsByPage(
      successes,
      fieldPositionLookup,
      contractDocumentOrder
    ),
    organizationIgnoredRules: sortValidationItemsByPage(
      organizationIgnoredRules,
      fieldPositionLookup,
      contractDocumentOrder
    ),
  };
}

/**
 * Gets error and success counts for a specific contract document.
 *
 * This function filters out hidden errors based on the following matching rules:
 * - A review hides an error when ALL of these conditions are met:
 *   1. review.ruleUuid matches the error's ruleUuid
 *   2. review.contractDocumentUuid equals the provided contractDocumentUuid
 *   3. review.ingestionDocumentValidation.id equals the current validation's id (validation-version-specific hide)
 *   4. review.isHidden is true
 *
 * @param contractDocumentUuid - The UUID of the contract document to count errors for
 * @param validationData - The validation data containing document validations and results
 * @param hiddenDocumentValidationReviews - Optional array of review records marking errors as hidden
 * @returns Object containing counts of { errors, successes }
 */
function getErrorAndSuccessCountsForContractDocument(
  contractDocumentUuid: string,
  validationData: ComplianceValidationReportData,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[]
): { errors: number; successes: number } {
  if (!validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  let errorCount = 0;
  let successCount = 0;

  validationData.documentValidations.forEach(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiValidationResults =
        docValidation.aiValidationResults as unknown as
          | ValidationResult[]
          | undefined;
      const validationResults = docValidation.validationResults as unknown as
        | ValidationResult[]
        | undefined;

      const resultsToProcess =
        aiValidationResults && aiValidationResults.length > 0
          ? aiValidationResults
          : validationResults;

      if (resultsToProcess) {
        resultsToProcess.forEach((result) => {
          if (result.contractDocumentUuid === contractDocumentUuid) {
            if (result.isOrganizationIgnored) {
              return;
            }

            if (result.output) {
              // Check if this error is hidden based on matching ruleUuid, contractDocumentUuid, and validation ID
              const isHidden = hiddenDocumentValidationReviews?.some(
                (review) =>
                  review.ruleUuid === result.ruleUuid &&
                  review.contractDocumentUuid === contractDocumentUuid &&
                  review.ingestionDocumentValidation?.id === docValidation.id &&
                  review.isHidden
              );

              // Only count if not hidden
              if (!isHidden) {
                errorCount++;
              }
            } else {
              successCount++;
            }
          }
        });
      }
    }
  );

  return { errors: errorCount, successes: successCount };
}

/**
 * Gets error and success counts for an entire ingestion document (all its contract documents)
 *
 * @param inScopeContractDocumentUuids When present, only these fragments are
 * counted — the set a SkySlope checklist item's rule actually required
 * (BLO-14871). Absent means count every fragment, which is the whole-document
 * question and remains the right default for document- and folder-level
 * surfaces.
 */
export function getErrorAndSuccessCountsForIngestionDocument(
  document: {
    contractDocuments?:
      | readonly {
          id: string;
        }[]
      | null;
  },
  validationData: ComplianceValidationReportData,
  contractDocumentId?: string,
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[],
  inScopeContractDocumentUuids?: ReadonlySet<string>
): { errors: number; successes: number } {
  if (!document.contractDocuments || !validationData?.documentValidations) {
    return { errors: 0, successes: 0 };
  }

  // If contractDocumentId is provided, only count for that specific contract document
  if (contractDocumentId) {
    return getErrorAndSuccessCountsForContractDocument(
      contractDocumentId,
      validationData,
      hiddenDocumentValidationReviews
    );
  }

  // Narrowing here rather than inside the loop reduces work: each fragment
  // otherwise rescans every validation in the folder.
  const contractDocuments =
    inScopeContractDocumentUuids == null
      ? document.contractDocuments
      : document.contractDocuments.filter((contractDoc) =>
          inScopeContractDocumentUuids.has(contractDoc.id)
        );

  let totalErrorCount = 0;
  let totalSuccessCount = 0;

  contractDocuments.forEach((contractDoc) => {
    const counts = getErrorAndSuccessCountsForContractDocument(
      contractDoc.id,
      validationData,
      hiddenDocumentValidationReviews
    );
    totalErrorCount += counts.errors;
    totalSuccessCount += counts.successes;
  });

  return { errors: totalErrorCount, successes: totalSuccessCount };
}

/**
 * Converts a field key into a human-readable label.
 *
 * Transforms nested field keys by replacing structural characters with readable separators
 * and applying title case formatting.
 *
 * @param key - The field key to transform (e.g., "buyer[0].name_first")
 * @returns A human-readable label (e.g., "Buyer > 0 Name | First")
 *
 * @example
 * friendlifyLabel("buyer[0].name_first") // "Buyer > 0 Name | First"
 * friendlifyLabel("seller.address_street") // "Seller | Address Street"
 */
const friendlifyLabel = (key: string) => {
  const replacedKey = key
    .replaceAll(']', ' ')
    .replaceAll('_', ' ')
    .replaceAll('.', ' | ')
    .replaceAll('[', ' > ')
    .replaceAll('  ', ' ');

  return titleCase(replacedKey, {
    titleTerminators: new Set([...TITLE_TERMINATORS, '|', '>']),
  });
};

/**
 * Groups and formats input values for display in tooltips
 */

/**
 * Strips -repositioned suffix from a fieldUuid if present
 */
function cleanFieldUuid(fieldUuid: string): string {
  return fieldUuid.replace(/-repositioned$/, '');
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue
 */
function isMultiFieldValue(value: unknown): value is MultiFieldValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'fieldUuid' in value &&
    'value' in value &&
    typeof value.fieldUuid === 'string' &&
    typeof value.value === 'string'
  );
}

/**
 * Type guard to check if an unknown value is a MultiFieldValue array
 */
function isMultiFieldValueArray(value: unknown): value is MultiFieldValue[] {
  return (
    Array.isArray(value) && value.length > 0 && isMultiFieldValue(value[0])
  );
}

/**
 * Extracts field UUIDs from a MultiFieldValue array value
 * Returns array of UUIDs if the value is a valid MultiFieldValue array, null otherwise
 */
export function extractFieldUuidsFromValue(value: unknown): string[] | null {
  try {
    let parsed: unknown;

    // If value is already an object/array, use it directly
    if (typeof value === 'object' && value !== null) {
      parsed = value;
    } else if (typeof value === 'string') {
      parsed = JSON.parse(value);
    } else {
      return null;
    }

    if (isMultiFieldValueArray(parsed)) {
      // Strip -repositioned suffix from field UUIDs as a safety measure
      return parsed.map((item) => cleanFieldUuid(item.fieldUuid));
    }
  } catch (e) {
    // Not JSON or not a MultiFieldValue array
  }

  return null;
}

/**
 * Parses a value that might be a JSON-serialized MultiFieldValue array
 * Returns formatted string showing each field's status
 */
function parseMultiFieldValue(value: string): string | null {
  try {
    const parsed: unknown = JSON.parse(value);

    if (isMultiFieldValueArray(parsed)) {
      // If only one field, just show status without number
      // If multiple fields, show number and status (e.g., "1: Signed, 2: Unsigned")
      if (parsed.length === 1) {
        const status = parsed[0].value ? 'Signed' : 'Unsigned';

        return status;
      }

      return parsed
        .map((item, index) => {
          const status = item.value ? 'Signed' : 'Unsigned';

          return `${index + 1}: ${status}`;
        })
        .join(', ');
    }
  } catch (e) {
    // Not JSON or not a MultiFieldValue array, return null
  }

  return null;
}

export function formatInputValuesForTooltip(
  inputValues?: {
    variableSource?: VariableSource;
    fieldKey: string;
    value: string;
  }[],
  document?: GetIngestionDocumentFragment | null,
  variant: 'internal' | 'external' = 'external'
): {
  fromContract: Array<{ label: string; value: string }>;
  externalReference: Array<{ label: string; value: string }>;
} {
  if (!inputValues || inputValues.length === 0) {
    return { fromContract: [], externalReference: [] };
  }

  const fieldKeyToLabel = createFieldKeyToLabelMapping(document);

  const fromContract: Array<{ label: string; value: string }> = [];
  const externalReference: Array<{ label: string; value: string }> = [];

  const shouldShowInputValue = (fieldKey: string) => {
    if (fieldKey === 'ingestion.documentFormNames') {
      return variant === 'internal';
    }

    return true;
  };

  inputValues.forEach((input) => {
    if (!shouldShowInputValue(input.fieldKey)) {
      return;
    }
    const friendlyLabel =
      fieldKeyToLabel[input.fieldKey] || friendlifyLabel(input.fieldKey);

    // Value is guaranteed to be a string from getValidationResultsForDocument
    const stringValue = input.value;

    // Try to parse as MultiFieldValue array and format it for display
    const multiFieldDisplay = parseMultiFieldValue(stringValue);

    const friendlyValue =
      multiFieldDisplay ||
      (!stringValue || stringValue === 'undefined' ? '-' : stringValue);

    const displayItem = { label: friendlyLabel, value: friendlyValue };

    const source = input.variableSource ?? VariableSource.FIELD;

    if (source === VariableSource.FIELD) {
      if (fieldKeyToLabel[input.fieldKey]) {
        fromContract.push(displayItem);
      }
    } else {
      // All other sources are considered external reference data
      externalReference.push(displayItem);
    }
  });

  return { fromContract, externalReference };
}

export interface ImprovedField {
  fieldKey: string;
  fieldLabel: string;
  reductionCount: number;
}

export interface AnalysisWithFields {
  analysis: string;
  contractDocumentUuid: string;
  relatedFieldKeys?: string[];
}

export interface ValidationComparisonData {
  aiErrorCount: number;
  regularErrorCount: number;
  aiWarningCount: number;
  regularWarningCount: number;
  reductionPercentage: number;
  hasAiData: boolean;
  latestValidationDate?: string;
  aiAnalysisData?: string[];
  improvedFieldsList?: ImprovedField[];
  aiAnalysisWithFields?: AnalysisWithFields[];
}

/**
 * Calculates percentage reduction in validation issues
 */
function calculateReductionPercentage(
  totalRegularIssues: number,
  totalAiIssues: number
): number {
  if (totalRegularIssues === 0) return 0;

  return Math.round(
    ((totalRegularIssues - totalAiIssues) / totalRegularIssues) * 100
  );
}

/**
 * Extracts fields that were improved by AI processing
 */
function extractImprovedFields(
  fieldComparisons: Array<{
    fieldKey: string;
    fieldLabel?: string;
    changeType: string;
    reductionCount: number;
  }>
): ImprovedField[] {
  return fieldComparisons
    .filter(
      (field) =>
        field.changeType === 'removed' ||
        (field.changeType === 'modified' && field.reductionCount > 0)
    )
    .map((field) => ({
      fieldKey: field.fieldKey,
      fieldLabel: field.fieldLabel || field.fieldKey,
      reductionCount: field.reductionCount,
    }));
}

/**
 * Associates AI analysis insights with related field keys
 */
function buildAnalysisWithFields(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation,
  fieldComparisons: Array<{
    fieldKey: string;
    changeType: string;
    reductionCount: number;
    aiResults: ValidationResult[];
  }>
): AnalysisWithFields[] {
  const analysisWithFields: AnalysisWithFields[] = [];

  const analyses = validationForDocument.aiAnalysis as
    | Array<{
        contractDocumentUuid: string;
        analysis: string;
      }>
    | undefined;

  if (!analyses) return [];

  analyses.forEach((analysisItem) => {
    const relatedFieldKeys = fieldComparisons
      .filter(
        (field) =>
          (field.changeType === 'removed' ||
            (field.changeType === 'modified' && field.reductionCount > 0)) &&
          field.aiResults.some(
            (result) =>
              result.contractDocumentUuid === analysisItem.contractDocumentUuid
          )
      )
      .map((field) => field.fieldKey);

    analysisWithFields.push({
      analysis: analysisItem.analysis,
      contractDocumentUuid: analysisItem.contractDocumentUuid,
      relatedFieldKeys:
        relatedFieldKeys.length > 0 ? relatedFieldKeys : undefined,
    });
  });

  return analysisWithFields;
}

/**
 * Gets the validation for a specific IngestionDocument.
 */
export function getValidationForDocument(
  document: GetIngestionDocumentFragment,
  validationData?: ComplianceValidationReportData
): ComplianceIngestionDocumentValidation | null {
  if (!validationData?.documentValidations) {
    return null;
  }

  const validations = validationData.documentValidations.filter(
    (validation) => document.id === validation.ingestionDocument.id
  );

  if (validations.length === 0) {
    return null;
  }

  if (validations.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`Multiple validations found for document ${document.id}.`);
  }

  return validations[0];
}

/**
 * Compares AI vs regular validation results for a specific document
 */
export function getValidationComparisonData(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation
): ValidationComparisonData | null {
  const comparisonData: ValidationComparisonData = {
    aiErrorCount: 0,
    aiWarningCount: 0,
    regularErrorCount: 0,
    regularWarningCount: 0,
    reductionPercentage: 0,
    hasAiData: false,
  };

  let hasAiProcessing = false;
  let mostRecentValidationDate: string | undefined;

  if (!!validationForDocument.aiValidationResults?.length) {
    hasAiProcessing = true;
  }

  if (validationForDocument.updatedAt) {
    mostRecentValidationDate = validationForDocument.updatedAt as string;
  }

  comparisonData.aiErrorCount = validationForDocument.aiErrorCount;
  comparisonData.aiWarningCount = validationForDocument.aiWarningCount;
  comparisonData.regularErrorCount = validationForDocument.errorCount;
  comparisonData.regularWarningCount = validationForDocument.warningCount;

  const totalRegularIssues =
    comparisonData.regularErrorCount + comparisonData.regularWarningCount;
  const totalAiIssues =
    comparisonData.aiErrorCount + comparisonData.aiWarningCount;
  const improvementPercentage = calculateReductionPercentage(
    totalRegularIssues,
    totalAiIssues
  );

  const fieldComparisonData = getFieldLevelComparisonData(
    document,
    validationForDocument
  );
  const improvedFields = extractImprovedFields(
    fieldComparisonData.fieldComparisons
  );
  const analysisWithFieldContext = buildAnalysisWithFields(
    document,
    validationForDocument,
    fieldComparisonData.fieldComparisons
  );

  return {
    ...comparisonData,
    reductionPercentage: improvementPercentage,
    hasAiData: hasAiProcessing,
    latestValidationDate: mostRecentValidationDate,
    improvedFieldsList: improvedFields.length > 0 ? improvedFields : undefined,
    aiAnalysisWithFields:
      analysisWithFieldContext.length > 0
        ? analysisWithFieldContext
        : undefined,
  };
}

/**
 * Gets validation timestamps from document validations
 */
export function getValidationTimestamps(
  validationForDocument: ComplianceIngestionDocumentValidation
): {
  createdAt?: string;
  updatedAt?: string;
  isUpToDate: boolean;
} {
  if (!validationForDocument) {
    return { isUpToDate: false };
  }

  // TODO: BLO-2146: use validatedAt and aiValidationUpdatedAt
  // Get the most recent validation
  const mostRecent = validationForDocument.updatedAt as string;

  if (!mostRecent) {
    return { isUpToDate: false };
  }

  const updatedAt = mostRecent;
  const createdAt = validationForDocument.createdAt as string;

  // Consider validation up to date if it's less than 24 hours old
  const isUpToDate = updatedAt
    ? new Date().getTime() - new Date(updatedAt).getTime() < 24 * 60 * 60 * 1000
    : false;

  return {
    createdAt,
    updatedAt,
    isUpToDate,
  };
}

export function hasDocumentBeenValidated(
  document: ExternalIngestionDocumentFragment,
  validationData: ComplianceValidationReportData
): boolean {
  if (!validationData?.documentValidations) {
    return false;
  }

  return validationData.documentValidations.some(
    (docValidation: ComplianceIngestionDocumentValidation) => {
      const aiResults = docValidation.aiValidationResults as unknown as
        | { ingestionDocumentUuid?: string }[]
        | undefined;
      const regularResults = docValidation.validationResults as unknown as
        | { ingestionDocumentUuid?: string }[]
        | undefined;
      const results = aiResults?.length ? aiResults : regularResults;

      return results?.some(
        (result) => result.ingestionDocumentUuid === document.id
      );
    }
  );
}

/**
 * Whether an auto-approved document has a frozen decision-time validation.
 * External validation fragments cannot carry this internal-only field, so the
 * result remains false on customer surfaces without a separate variant flag.
 */
export function hasDecisionTimeValidationSnapshot(
  document: Pick<ExternalIngestionDocumentFragment, 'id' | 'wasAutoApproved'>,
  validationData: ComplianceValidationReportData
): boolean {
  if (!document.wasAutoApproved || !validationData) {
    return false;
  }

  return validationData.documentValidations.some(
    (validation: ComplianceIngestionDocumentValidation) =>
      'decisionTimeValidationSnapshot' in validation &&
      validation.isCurrent &&
      validation.ingestionDocument.id === document.id &&
      validation.decisionTimeValidationSnapshot?.decision ===
        ComplianceAutoApprovalAttemptDecision.APPROVE
  );
}

export function hasIngestionDocumentBeenClassified(
  document: ExternalIngestionDocumentFragment
): boolean {
  return (document.contractDocuments?.length || 0) > 0;
}

/**
 * Gets display name for a contract document
 *
 * @param contractDoc - The contract document to get display name for
 * @returns Formatted display name like "Form - Name" or fallback to category
 */
/**
 * The form code and human name of a contract document, kept separate so a
 * caller can render the code as its own chip. Mirrors the fallback order of
 * {@link getContractDocDisplayName}: a fragment classified only by category
 * has no code, and its category stands in for the name.
 */
export function getContractDocDisplayParts(contractDoc?: {
  documentCategory?: string;
  documentVersion?: {
    document?: {
      form?: string;
      name?: string;
    };
  };
}): { formCode: string | null; formName: string } {
  const docForm = contractDoc?.documentVersion?.document?.form;
  const docName = contractDoc?.documentVersion?.document?.name;

  if (docForm && docName) {
    return { formCode: docForm, formName: docName };
  }

  return {
    formCode: null,
    formName: contractDoc?.documentCategory || 'Unknown Document',
  };
}

export function getContractDocDisplayName(contractDoc?: {
  documentCategory?: string;
  documentVersion?: {
    document?: {
      form?: string;
      name?: string;
    };
  };
}): string {
  const docForm = contractDoc?.documentVersion?.document?.form;
  const docName = contractDoc?.documentVersion?.document?.name;
  const fallbackName = contractDoc?.documentCategory;

  if (docForm && docName) {
    return `${docForm} - ${docName}`;
  }

  return fallbackName || 'Unknown Document';
}
